"use client";

import React from "react";
import { MessageSquare, Compass, Shield, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Consultation",
  icon: MessageSquare,
  description: "We begin with a private conversation to understand your concerns and specific needs, ensuring you feel heard and understood."
}, {
  title: "Strategy",
  icon: Compass,
  description: "Our experts design a discreet, ethical verification plan tailored to your situation, focusing on clarity and legal compliance."
}, {
  title: "Verification",
  icon: Shield,
  description: "We conduct thorough, confidential checks using our professional network and resources, maintaining the highest standards of integrity."
}, {
  title: "Report Delivery",
  icon: Mail,
  description: "You receive a comprehensive, clear report that empowers you to make informed decisions with confidence and peace of mind."
}];
export default function TheJourneyToClarity() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-journey-to-clarity" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--primary)_0%,_transparent_30%)] opacity-5 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-medium text-primary mb-6 tracking-tight">
            The Journey to Clarity
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed">
            A transparent, respectful process designed to provide you with the
            truth you deserve, every step of the way.
          </p>
        </div>

        <div ref={ref} className={cn("relative", isVisible ? "opacity-100" : "opacity-0 transition-opacity duration-1000")}>
          {}
          <div className="hidden md:block absolute top-12 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent -z-10" />
          
          {}
          <div className="md:hidden absolute left-1/2 top-0 bottom-0 w-[1px] -translate-x-1/2 bg-gradient-to-b from-transparent via-primary/40 to-transparent -z-10" />

          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-6">
            {steps.map((step, i) => <div key={i} data-index={i} className={cn("relative flex flex-col items-center text-center group transition-all duration-700 ease-out", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")} style={{
            transitionDelay: `${i * 200}ms`
          }}>
                {}
                <div className="relative mb-8">
                  <div className="w-24 h-24 rounded-full bg-background border border-primary/30 flex items-center justify-center shadow-[0_0_20px_-5px_rgba(var(--primary),0.15)] group-hover:border-primary/60 group-hover:shadow-[0_0_25px_-5px_rgba(var(--primary),0.3)] transition-all duration-500">
                    <step.icon className="w-10 h-10 text-primary stroke-[1.5]" />
                  </div>
                  {}
                  <div className="absolute top-1/2 -translate-y-1/2 -left-4 w-2 h-2 rounded-full bg-primary/20 hidden md:block opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-1/2 -translate-y-1/2 -right-4 w-2 h-2 rounded-full bg-primary/20 hidden md:block opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                {}
                <Card className="bg-transparent border-none shadow-none p-0 w-full">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="text-xl font-serif text-primary tracking-wide">
                      {step.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <CardDescription className="text-foreground/80 text-base leading-relaxed">
                      {step.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}